/* ═══════════════════════════════════════════════════════════════════════════
   VRG-739 — NARRATION RE-SPEND: what saving an edit to a narrated article
   would cost to re-narrate, before anything is sent to ElevenLabs.

   Spencer, 2026-10-23: "whenever an article or content is updated, on save we
   confirm the re-spend of narrating the edited sections."

   ── THE PRODUCER IS VERA-WEBSITE ─────────────────────────────────────────
   Narration is generated by vera-website's scripts/generate-article-audio.mjs.
   That script, and the page that decides whether to show the player, agree on
   two things this file has to agree with TO THE CHARACTER:
     · the SECTIONS (lib/article-narration.ts): the intro (title, lead, any
       block before the first h2), then one per h2; each section split into
       request chunks of at most the model's per-request limit, between
       segments, joined with joinSegments;
     · the CACHE KEY of every chunk: sha256(JSON.stringify({ mode, text,
       voice, model, settings, format, rules })), in exactly that key order.
       A rerun bills only chunks whose key is new; a changed voice, model,
       settings, output format or pronunciation rule changes every key.
   vera-2 cannot import that repo, so the rules are PORTED here, verbatim
   where they are code. The parity proof is test/narration-respend.guard.spec.ts
   against test/fixtures/narration/resale-royalties-for-brands.json, which
   apps/platform/scripts/generate-narration-fixture.mjs writes from the
   website's OWN functions and its paid chunk cache: the port must produce the
   website's sections, the manifest's contentHash, and the 8 cache keys the
   paid run wrote. The same fixture records the sha256 of each website file
   mirrored here; on a machine with that checkout the guard fails when one of
   them moved, which is the signal to re-port and regenerate.

   ── WHAT A PLAN SAYS ─────────────────────────────────────────────────────
   planRespend(previous, edited) chunks both versions, keys every chunk, and
   bills an edited chunk only when its key is not among the previous
   version's keys (plus any keys the caller knows are cached). That is the
   script's own rule, so the figure is what the script would bill, not an
   estimate of it. Neighbouring text sent as previous_text / next_text for a
   smooth join is not part of any key and is not counted (the script's
   dry-run does not count it either).

   This file imports nothing at runtime: the platform's node guards load it
   type-stripped, and the eventual server-side job can lift it unchanged.
   Hashing is WebCrypto (crypto.subtle), present in browsers (secure
   contexts: https and localhost) and in Node 19+, so every hash is async.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── The block model (port of vera-website content/article-blocks.ts) ───── */

export type TimelineStatus = 'law' | 'indicative'
export type FitStatus = 'today' | 'not-today' | 'set-by-act'

/** The narrated fields of the website's InsightBlock. Cites and eyebrows are
 *  allowed (the website's blocks carry them) and ignored: they are not read. */
export type NarrationBlock =
  | { type: 'heading'; text: string; eyebrow?: string }
  | { type: 'paragraph'; text: string; cites?: string[] }
  | { type: 'list'; items: string[] }
  | { type: 'facts'; items: { label: string; value: string; cites?: string[] }[] }
  | { type: 'timeline'; items: { when: string; status: TimelineStatus; what: string; cites?: string[] }[] }
  | { type: 'status'; items: { title: string; status: FitStatus; text: string; cites?: string[] }[] }
  | { type: 'cards'; items: { term: string; detail: string; cites?: string[] }[] }

export const TIMELINE_STATUS_LABEL: Record<TimelineStatus, string> = {
  law: 'In law',
  indicative: 'Indicative',
}

export const FIT_STATUS_LABEL: Record<FitStatus, string> = {
  today: 'In Vera today',
  'not-today': 'Not in Vera today',
  'set-by-act': 'Decided by each act',
}

/* ── Sections (port of vera-website lib/article-narration.ts) ──────────── */

/** The website's NARRATION_VERSION. Bumped there when the reading rules change. */
export const NARRATION_VERSION = 1

export type NarrationTarget =
  | { kind: 'title' }
  | { kind: 'lead' }
  | { kind: 'block'; block: number; part: number }

export type NarrationSegment = { target: NarrationTarget; text: string }

export type NarrationSection = {
  /** "intro", or the h2's text. */
  label: string
  segments: NarrationSegment[]
}

/** As scripts/lib/article-audio-pieces.mjs maps an article: title, lead (an
 *  article's excerpt, an explainer's lead), body blocks, format. */
export type NarrationInput = {
  title: string
  lead?: string | null
  blocks: readonly NarrationBlock[]
  /** "video" (an interview) is never narrated. */
  format?: string | null
}

export function blockNarration(b: NarrationBlock): string[] {
  switch (b.type) {
    case 'heading':
    case 'paragraph':
      return [b.text]
    case 'list':
      return [...b.items]
    case 'facts':
      return b.items.flatMap((i) => [i.label, i.value])
    case 'timeline':
      return b.items.flatMap((i) => [i.when, TIMELINE_STATUS_LABEL[i.status], i.what])
    case 'status':
      return b.items.flatMap((i) => [FIT_STATUS_LABEL[i.status], i.title, i.text])
    case 'cards':
      return b.items.flatMap((i) => [i.term, i.detail])
  }
}

export function narrationSections(a: NarrationInput): NarrationSection[] {
  if (a.format === 'video') return []
  const sections: NarrationSection[] = [{ label: 'intro', segments: [] }]
  const push = (s: NarrationSegment) => {
    const text = s.text.trim()
    if (text) sections[sections.length - 1].segments.push({ target: s.target, text: s.text })
  }
  push({ target: { kind: 'title' }, text: a.title })
  if (a.lead) push({ target: { kind: 'lead' }, text: a.lead })
  a.blocks.forEach((b, block) => {
    if (b.type === 'heading') sections.push({ label: b.text, segments: [] })
    blockNarration(b).forEach((text, part) => push({ target: { kind: 'block', block, part }, text }))
  })
  return sections.filter((s) => s.segments.length > 0)
}

export function narrationFingerprint(sections: readonly NarrationSection[]): string {
  return JSON.stringify([NARRATION_VERSION, sections.map((s) => s.segments.map((g) => [g.target, g.text]))])
}

/** One request's text: a full stop if the previous segment lacks terminal
 *  punctuation, then a blank line, between segments. */
export function joinSegments(segments: readonly NarrationSegment[]): { text: string; offsets: number[] } {
  let text = ''
  const offsets: number[] = []
  segments.forEach((s, i) => {
    if (i > 0) text += /[.!?:;…"”’)]\s*$/.test(segments[i - 1].text) ? '\n\n' : '.\n\n'
    offsets.push(text.length)
    text += s.text
  })
  return { text, offsets }
}

/* ── Request settings (port of vera-website lib/article-audio-config.ts) ── */

/** Characters per request, per model (ElevenLabs docs, verified by the website 2026-10-22). */
export const MODEL_CHAR_LIMIT: Record<string, number> = {
  eleven_multilingual_v2: 10_000,
  eleven_v4: 10_000,
  eleven_v3: 5_000,
  eleven_flash_v2_5: 40_000,
}

export const DEFAULT_MODEL_ID = 'eleven_multilingual_v2'

/** Sent with every real request and part of every real key. */
export const VOICE_SETTINGS = {
  stability: 0.5,
  similarity_boost: 0.75,
  style: 0,
  use_speaker_boost: true,
  speed: 1,
} as const

/** What the script requests when ffmpeg is installed (it always is where the paid run happened). */
export const REQUEST_OUTPUT_FORMAT = 'mp3_44100_128'
/** What it requests WITHOUT ffmpeg. Part of the key, so a run on a machine without ffmpeg re-bills everything. */
export const REQUEST_OUTPUT_FORMAT_NO_FFMPEG = 'mp3_44100_64'

/**
 * Price per model. Only what the website states with a source: Multilingual v2 is 1 credit per
 * character on subscription plans and $0.08 per 1K characters on the API (elevenlabs.io/pricing/api,
 * as recorded in lib/article-audio-config.ts). A plan for any other model throws rather than
 * guess; add its verified price here first.
 */
export const MODEL_PRICING: Record<string, { creditsPerChar: number; usdPer1kChars: number }> = {
  eleven_multilingual_v2: { creditsPerChar: 1, usdPer1kChars: 0.08 },
}

export type PronunciationRule = { type: 'alias'; string_to_replace: string; alias: string }

/** Everything that goes into a chunk's cache key besides its text, plus the chunking limit. */
export type NarrationProfile = {
  mode: 'real' | 'mock'
  voice: string
  /** As keyed: the model id for a real run, "say" for a mock one. */
  model: string
  settings: unknown
  format: string
  rules: readonly PronunciationRule[]
  /** Per-request character limit used to chunk. NOT part of the key (the script takes it from the configured model even in mock mode). */
  charLimit: number
}

/** A real (paid) run's profile, as the script builds it. */
export function realProfile(o: {
  voice: string
  model?: string
  rules?: readonly PronunciationRule[]
  ffmpeg?: boolean
}): NarrationProfile {
  const model = o.model ?? DEFAULT_MODEL_ID
  const charLimit = MODEL_CHAR_LIMIT[model]
  if (!charLimit) throw new Error(`Unknown model ${model}: add its verified per-request limit to MODEL_CHAR_LIMIT first.`)
  return {
    mode: 'real',
    voice: o.voice,
    model,
    settings: VOICE_SETTINGS,
    format: o.ffmpeg === false ? REQUEST_OUTPUT_FORMAT_NO_FFMPEG : REQUEST_OUTPUT_FORMAT,
    rules: o.rules ?? [],
    charLimit,
  }
}

/** The script's --mock profile (macOS `say`, free). */
export function mockProfile(o: { sayVoice?: string; model?: string; ffmpeg?: boolean } = {}): NarrationProfile {
  const charLimit = MODEL_CHAR_LIMIT[o.model ?? DEFAULT_MODEL_ID]
  if (!charLimit) throw new Error(`Unknown model ${o.model}`)
  return {
    mode: 'mock',
    voice: `say:${o.sayVoice ?? 'Samantha'}`,
    model: 'say',
    settings: { rate: 185 },
    format: o.ffmpeg === false ? REQUEST_OUTPUT_FORMAT_NO_FFMPEG : REQUEST_OUTPUT_FORMAT,
    rules: [],
    charLimit,
  }
}

/* ── Hashing ───────────────────────────────────────────────────────────── */

async function sha256Hex(s: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle
  if (!subtle) throw new Error('crypto.subtle is unavailable (needs https or localhost, or Node 19+)')
  const buf = await subtle.digest('SHA-256', new TextEncoder().encode(s))
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('')
}

/** The website's narrationHash: the 16-hex contentHash a manifest entry and its MP3 are tied to. */
export async function narrationHash(a: NarrationInput): Promise<string> {
  return (await sha256Hex(narrationFingerprint(narrationSections(a)))).slice(0, 16)
}

/** The profile fields that enter a key, as one comparable string. */
const keyedProfile = (p: NarrationProfile) =>
  JSON.stringify({ mode: p.mode, voice: p.voice, model: p.model, settings: p.settings, format: p.format, rules: p.rules })

/** A chunk's cache key, exactly as the script's `sha({ mode, text, voice, model, settings, format, rules })`. */
export function chunkKey(p: NarrationProfile, text: string): Promise<string> {
  return sha256Hex(
    JSON.stringify({ mode: p.mode, text, voice: p.voice, model: p.model, settings: p.settings, format: p.format, rules: p.rules }),
  )
}

/* ── Chunks ────────────────────────────────────────────────────────────── */

export type NarrationChunk = {
  section: number
  label: string
  text: string
  key: string
}

/** The script's chunkSection: greedy, split only between segments. */
function chunkSection(segs: readonly NarrationSegment[], limit: number): NarrationSegment[][] {
  const out: NarrationSegment[][] = []
  let cur: NarrationSegment[] = []
  for (const s of segs) {
    if (s.text.length > limit) throw new Error(`One segment is ${s.text.length} chars, over the ${limit} limit: split the paragraph.`)
    const next = [...cur, s]
    if (cur.length && joinSegments(next).text.length > limit) {
      out.push(cur)
      cur = [s]
    } else cur = next
  }
  if (cur.length) out.push(cur)
  return out
}

/** Every request chunk of a piece, keyed, in the order the script sends them. */
export async function narrationChunks(a: NarrationInput, p: NarrationProfile): Promise<NarrationChunk[]> {
  const chunks: NarrationChunk[] = []
  for (const [si, sec] of narrationSections(a).entries()) {
    for (const group of chunkSection(sec.segments, p.charLimit)) {
      const { text } = joinSegments(group)
      chunks.push({ section: si, label: sec.label, text, key: await chunkKey(p, text) })
    }
  }
  return chunks
}

/* ── The plan ──────────────────────────────────────────────────────────── */

export type SectionPlan = {
  /** Position in the edited version. */
  index: number
  label: string
  /** The section's request characters (what a full re-narration of it would bill). */
  chars: number
  /** Characters of this section's chunks that are NOT cached, i.e. billed by a re-narration. */
  freshChars: number
  /** freshChars at the model's credit rate. */
  credits: number
  renarrate: boolean
}

export type RespendPlan = {
  /** The edited version's contentHash, and the narrated one's. Different ⇒ the current audio no longer plays on the site. */
  contentHash: string
  previousContentHash: string
  textChanged: boolean
  /** The key profile (voice, model, settings, format, rules) differs from the narrated one: every chunk is new. */
  profileChanged: boolean
  sections: SectionPlan[]
  /** Sections of the edited version that would be sent to ElevenLabs. */
  changedSections: SectionPlan[]
  /** Narrated sections that are gone: no audio of theirs is used and no edited section has their heading. Cost nothing. */
  removedSections: string[]
  /** Requests (chunks) a re-narration sends. */
  requests: number
  /** Characters a re-narration sends: the credits basis. */
  chars: number
  credits: number
  usd: number
  /** The whole edited narration's characters, for "of N". */
  totalChars: number
  /** True when re-narrating spends anything: only then is the confirmation needed. */
  needsConfirmation: boolean
}

export type NarratedVersion = { input: NarrationInput; profile: NarrationProfile }

/**
 * What re-narrating `edited` costs, given the version that was narrated.
 *
 * `cachedKeys`: any keys the caller knows are in the chunk cache beyond the narrated version's own
 * (an older version, the private gs://vera-narration-cache backup listing). Optional: without it
 * the plan assumes exactly the narrated version's chunks are cached. That over-counts when older
 * chunks also survive, and UNDER-counts if the narrated chunks are gone from the cache the job
 * runs against (the script then re-bills them), so the job must re-plan against the real cache
 * before it sends anything.
 */
export async function planRespend(
  previous: NarratedVersion,
  edited: NarratedVersion,
  cachedKeys: Iterable<string> = [],
): Promise<RespendPlan> {
  const pricing = edited.profile.mode === 'mock' ? { creditsPerChar: 0, usdPer1kChars: 0 } : MODEL_PRICING[edited.profile.model]
  if (!pricing) throw new Error(`No verified price for model ${edited.profile.model}: add it to MODEL_PRICING first.`)

  const [prevChunks, nextChunks, contentHash, previousContentHash] = await Promise.all([
    narrationChunks(previous.input, previous.profile),
    narrationChunks(edited.input, edited.profile),
    narrationHash(edited.input),
    narrationHash(previous.input),
  ])
  const cached = new Set([...prevChunks.map((c) => c.key), ...cachedKeys])
  const used = new Set(nextChunks.map((c) => c.key))

  const sections: SectionPlan[] = []
  for (const c of nextChunks) {
    const s = (sections[c.section] ??= { index: c.section, label: c.label, chars: 0, freshChars: 0, credits: 0, renarrate: false })
    s.chars += c.text.length
    if (!cached.has(c.key)) {
      s.freshChars += c.text.length
      s.credits += c.text.length * pricing.creditsPerChar
      s.renarrate = true
    }
  }
  const fresh = nextChunks.filter((c) => !cached.has(c.key))
  const chars = fresh.reduce((n, c) => n + c.text.length, 0)

  // A narrated section is REMOVED when none of its audio is used and no edited section carries
  // its heading (an edited section under the same heading is "changed", not removed).
  const labels = new Set(sections.map((s) => s.label))
  const removed: string[] = []
  for (const [si, label] of new Map(prevChunks.map((c) => [c.section, c.label]))) {
    if (!labels.has(label) && !prevChunks.some((c) => c.section === si && used.has(c.key))) removed.push(label)
  }

  const credits = chars * pricing.creditsPerChar
  return {
    contentHash,
    previousContentHash,
    textChanged: contentHash !== previousContentHash,
    profileChanged: keyedProfile(previous.profile) !== keyedProfile(edited.profile),
    sections,
    changedSections: sections.filter((s) => s.renarrate),
    removedSections: removed,
    requests: fresh.length,
    chars,
    credits,
    usd: (chars / 1000) * pricing.usdPer1kChars,
    totalChars: nextChunks.reduce((n, c) => n + c.text.length, 0),
    needsConfirmation: credits > 0,
  }
}

/* ── Copy (the confirmation's words; asserted verbatim by the guard) ───── */

const n = (v: number) => v.toLocaleString('en-US')
const plural = (k: number, one: string, many = `${one}s`) => `${n(k)} ${k === 1 ? one : many}`

/** "$0.17"; "<$0.01" for a spend that rounds to nothing, so a real cost never reads as free. */
export function formatUsd(usd: number): string {
  if (usd > 0 && usd < 0.005) return '<$0.01'
  return `$${usd.toFixed(2)}`
}

/** A section's name as the dialog lists it: the intro has no heading of its own. */
export const sectionName = (label: string) => (label === 'intro' ? 'Introduction (title and lead)' : label)

/** "3 sections changed · 2,140 credits · ≈$0.17" */
export function respendSummary(p: Pick<RespendPlan, 'changedSections' | 'credits' | 'usd'>): string {
  return `${plural(p.changedSections.length, 'section')} changed · ${plural(p.credits, 'credit')} · ≈${formatUsd(p.usd)}`
}

export const RESPEND_TITLE = 'Re-narrate the edited sections?'
export const RESPEND_CONFIRM_LABEL = 'Re-narrate changed sections'
export const RESPEND_SKIP_LABEL = 'Save without re-narrating'

/** Context above the consequence: what changed, and what is reused for free. */
export function respendPreamble(title: string, p: RespendPlan): string {
  if (p.profileChanged) {
    return `"${title}" is narrated, and the voice or its settings changed since then, so every section is recorded again. Nothing from the current recording can be reused.`
  }
  const reused = p.sections.length - p.changedSections.length
  return `"${title}" is narrated. Your edit changed what it reads in ${plural(p.changedSections.length, 'section')}. ${
    reused === 0 ? 'No section is reused.' : `The other ${plural(reused, 'section')} reuse the current recording at no cost.`
  }`
}

/** The consequence (the warning): the spend, and what skipping it means on the site. */
export function respendConsequence(p: RespendPlan): string {
  return `Re-narrating sends ${p.changedSections.length === 1 ? 'that section' : 'those sections'} to ElevenLabs and spends ${plural(
    p.credits,
    'credit',
  )} (≈${formatUsd(p.usd)}). If you save without re-narrating, the new text is saved and the website hides the Listen player until the article is narrated again.`
}
